import React from 'react';

import { Trans, useTranslation } from 'react-i18next';
import { Table } from '@bb-ui/react-library/dist/components/Table';
import { TableRow } from '@bb-ui/react-library/dist/components/TableRow';
import { TableCell } from '@bb-ui/react-library/dist/components/TableCell';
import { TableBody } from '@bb-ui/react-library/dist/components/TableBody';
import { TableHead } from '@bb-ui/react-library/dist/components/TableHead';
import {
    OutlineButton,
    DefaultButton,
    PrimaryButton,
} from '@bb-ui/react-library/dist/components/Button';
import { Dialog } from '@bb-ui/react-library/dist/components/Dialog';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { DialogContent } from '@bb-ui/react-library/dist/components/DialogContent';
import { DialogActions } from '@bb-ui/react-library/dist/components/DialogActions';
import { OffScreenAnnouncement } from '@bb-ui/react-library';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import { useApi } from '../../../../hooks/useApi';
import { Message } from '../../../Message/Message';
import { SkeletonTable } from '../../../Skeleton/SkeletonTable';
import { useUser } from '../../../../hooks/useUser';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { useAppLoadingContext } from '../../../../contexts/AppLoadingContext';
import { useFeatureContext } from '../../../../contexts/FeatureContext';
import { useAccessControl } from '../../../../hooks/useAccessControl';
import { CreateServiceAccountDialog } from './CreateServiceAccountDialog';
import { useStyles } from './SnowflakeServiceUsers.styles';
import {
    ServiceAccountListResponse,
    ServiceAccountResponse,
} from './ServiceUsers.types';

interface ColumnDescriptions {
    [key: string]: {
        showLabel: boolean;
        getValue: (serviceAccount: ServiceAccountResponse) => any;
    };
}

interface ResetPasswordResponse {
    url: string;
}

enum DialogState {
    closed,
    changePasswordConfirmation,
    changeEmail,
}

interface DisabledControlTooltipProps {
    children: React.ReactElement;
}

// Wraps a disabled control in a focusable span so its tooltip still shows on
// hover/keyboard focus - disabled elements don't fire those events on their own.
const FocusableTooltip: React.FC<
    DisabledControlTooltipProps & { title: string }
> = ({ title, children }) => (
    <Tooltip title={title}>
        <span tabIndex={0}>{children}</span>
    </Tooltip>
);

// Tooltip for a disabled control when the tenant is missing the premium licence.
export const UpgradePromptTooltip: React.FC<DisabledControlTooltipProps> = ({
    children,
}) => {
    const { t } = useTranslation();
    return (
        <FocusableTooltip title={t('settings.userManagement.upgradePrompt')}>
            {children}
        </FocusableTooltip>
    );
};

// Tooltip for a disabled control when the user's role isn't allowed to use it.
export const NoPermissionTooltip: React.FC<DisabledControlTooltipProps> = ({
    children,
}) => {
    const { t } = useTranslation();
    return (
        <FocusableTooltip
            title={t('settings.userManagement.noPermissionPrompt')}
        >
            {children}
        </FocusableTooltip>
    );
};

export const SnowflakeServiceUsers: React.FC = () => {
    const user = useUser();
    const { t } = useTranslation();
    const { api } = useAppConfigContext();
    const { setLoadingFlag } = useAppLoadingContext();
    const features = useFeatureContext();
    const access = useAccessControl();
    const classes = useStyles();

    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const multiServiceAccountEnabled =
        features?.multiServiceAccountEnabled ?? false;
    const manageAccess = access['developer/settings/service-users/manage'];
    const canManageServiceUsers = manageAccess.ok;
    // Only a missing licence is fixed by upgrading. Anyone else is blocked by
    // their role, so telling them to upgrade would be wrong.
    const DisabledCreateTooltip =
        manageAccess.reason === 'FEATURE_MISSING'
            ? UpgradePromptTooltip
            : NoPermissionTooltip;

    const serviceAccountsBaseUrl = `${api!.serviceAccountManagement}/v1/data/tenants/${user?.tenantIdentifier}/serviceAccounts`;

    const {
        data: serviceAccountListResponse,
        loading: serviceAccountListLoading,
        error: serviceAccountListError,
        execute: refetchServiceAccounts,
    } = useApi<ServiceAccountListResponse | ServiceAccountResponse[]>({
        url: serviceAccountsBaseUrl,
        useAuth: true,
        method: 'GET',
        // The post-create refetch re-issues this exact request, so the shared
        // axios-hooks cache would otherwise return the pre-create list.
        useCache: false,
    });

    // the old API returns the plain list; the new one wraps it with the quota
    const serviceAccountList = Array.isArray(serviceAccountListResponse)
        ? serviceAccountListResponse
        : serviceAccountListResponse?.serviceAccounts;

    const {
        data: resetPasswordLink,
        loading: resetPasswordLinkLoading,
        error: resetPasswordLinkError,
        execute: executeResetPasswordRequest,
    } = useApi<ResetPasswordResponse>({
        useAuth: true,
        executeType: 'manual',
        method: 'PATCH',
    });
    const [modalState, setModalState] = React.useState<DialogState>(
        DialogState.closed,
    );

    const [selectedServiceAccount, setSelectedServiceAccount] =
        React.useState<ServiceAccountResponse>();

    const tableColumns: ColumnDescriptions = {
        accountName: {
            showLabel: true,
            getValue: serviceAccount => serviceAccount.username,
        },
        disabled: {
            showLabel: true,
            getValue: serviceAccount =>
                t(
                    `settings.userManagement.userState.${
                        serviceAccount.disabled ? 'disabled' : 'enabled'
                    }`,
                ),
        },
        hasPassword: {
            showLabel: true,
            getValue: serviceAccount =>
                t(`general.${serviceAccount.hasPassword ? 'yes' : 'no'}`),
        },

        daysUntilExpiration: {
            showLabel: true,
            getValue: serviceAccount => serviceAccount.daysUntilExpiration,
        },

        expirationDate: {
            showLabel: true,
            getValue: serviceAccount => serviceAccount.expirationDate,
        },
        actions: {
            showLabel: true,
            getValue: function ResetPasswordButton(serviceAccount) {
                return (
                    <OutlineButton
                        data-testid={`${serviceAccount.username}-resetPasswordButton`}
                        onClick={() => {
                            setSelectedServiceAccount(serviceAccount);
                            setModalState(
                                DialogState.changePasswordConfirmation,
                            );
                        }}
                        aria-label={t(
                            'settings.userManagement.tableButtons.changePasswordFor',
                            { username: serviceAccount.username },
                        )}
                    >
                        {t(
                            'settings.userManagement.tableButtons.changePassword',
                        )}
                    </OutlineButton>
                );
            },
        },
    };

    React.useEffect(() => {
        if (resetPasswordLinkLoading) {
            setLoadingFlag('service-users.link-gen', resetPasswordLinkLoading);
        }
    }, [resetPasswordLinkLoading, setLoadingFlag]);

    React.useEffect(() => {
        if (resetPasswordLinkError) {
            setLoadingFlag('service-users.link-gen', false);
        }
    }, [resetPasswordLinkError, setLoadingFlag]);

    React.useEffect(() => {
        if (resetPasswordLink) {
            window.location.href = resetPasswordLink.url;
        }
    }, [resetPasswordLink]);

    const ResetPasswordDialogMemoized = React.memo(() => (
        <Dialog
            open={modalState !== DialogState.closed}
            maxWidth="md"
            onClose={() => setModalState(DialogState.closed)}
            aria-labelledby="reset-password-title"
        >
            <DialogTitle
                id="reset-password-title"
                onClose={() => setModalState(DialogState.closed)}
            >
                {modalState === DialogState.changePasswordConfirmation && (
                    <>
                        {/* prettier-ignore */}
                        <Trans i18nKey="settings.userManagement.resetPasswordModal.title">
                        You’re leaving <span lang="en">Blackboard Illuminate</span>
                    </Trans>
                    </>
                )}
            </DialogTitle>
            <DialogContent>
                {modalState === DialogState.changePasswordConfirmation && (
                    <>
                        {/* prettier-ignore */}
                        <Trans i18nKey="settings.userManagement.resetPasswordModal.description">
                            You’ll be redirected to <span lang="en">Snowflake</span> to complete your password change. Do you want to continue?
                        </Trans>
                        {resetPasswordLinkError && (
                            <Message
                                variant="error"
                                message={t('general.defaultPageError')}
                            />
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {modalState === DialogState.changePasswordConfirmation && (
                    <>
                        <DefaultButton
                            data-testid="resetPassword-cancel"
                            onClick={() => setModalState(DialogState.closed)}
                        >
                            {t('general.cancel')}
                        </DefaultButton>
                        <PrimaryButton
                            data-testid="resetPassword-confirm"
                            onClick={() => {
                                executeResetPasswordRequest({
                                    url: `${serviceAccountsBaseUrl}/${selectedServiceAccount?.username}/resetPassword`,
                                });
                            }}
                        >
                            {t('general.yes')}
                        </PrimaryButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    ));
    ResetPasswordDialogMemoized.displayName = 'ResetPasswordDialog';

    // A new account is re-read from the list endpoint rather than assembled from
    // the create response. Per open_api.yaml, AddServiceUserResponse only
    // guarantees tenantId and username; status, password and both expiry fields
    // are derived server-side on the read path alone (UserDynamoEntry.to_result,
    // whose only caller is GetServiceUsers). Rendering the create response as a
    // row therefore shows a brand-new account - which has no password and is
    // genuinely disabled - as Enabled, with both expiry cells empty.
    const handleAccountCreated = React.useCallback(() => {
        refetchServiceAccounts();
    }, [refetchServiceAccounts]);

    const serviceAccounts = serviceAccountList ?? [];

    return (
        <>
            <ResetPasswordDialogMemoized />
            {multiServiceAccountEnabled && (
                <>
                    <div className={classes.createServiceAccountRow}>
                        {canManageServiceUsers ? (
                            <button
                                type="button"
                                className={classes.createServiceAccountButton}
                                data-testid="create-service-account-button"
                                onClick={() => setCreateDialogOpen(true)}
                            >
                                {t('settings.userManagement.create.openButton')}
                            </button>
                        ) : (
                            <DisabledCreateTooltip>
                                <button
                                    type="button"
                                    className={
                                        classes.createServiceAccountButton
                                    }
                                    data-testid="create-service-account-button-disabled"
                                    disabled
                                    aria-disabled="true"
                                >
                                    {t(
                                        'settings.userManagement.create.openButton',
                                    )}
                                </button>
                            </DisabledCreateTooltip>
                        )}
                    </div>
                    <CreateServiceAccountDialog
                        open={createDialogOpen}
                        serviceAccountsBaseUrl={serviceAccountsBaseUrl}
                        onClose={() => setCreateDialogOpen(false)}
                        onCreated={handleAccountCreated}
                    />
                </>
            )}
            {serviceAccounts.length > 0 && (
                <div className={classes.tableContainer}>
                    <Table>
                        <TableHead>
                            <TableRow className={classes.tableHeaderRow}>
                                {Object.entries(tableColumns).map(
                                    ([key, column]) => (
                                        <TableCell
                                            key={key}
                                            role="columnheader"
                                            className={classes.tableHeader}
                                            aria-label={
                                                !column.showLabel
                                                    ? t(
                                                          `settings.userManagement.tableHeaders.${key}`,
                                                      )
                                                    : undefined
                                            }
                                        >
                                            {column.showLabel &&
                                                t(
                                                    `settings.userManagement.tableHeaders.${key}`,
                                                )}
                                        </TableCell>
                                    ),
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody data-testid="service-account-list">
                            {serviceAccounts.map(serviceAccount => (
                                <TableRow key={serviceAccount.username}>
                                    {Object.entries(tableColumns).map(
                                        ([key, column]) => (
                                            <TableCell
                                                key={`${serviceAccount.username}-${key}`}
                                                data-testid={`${serviceAccount.username}-${key}`}
                                            >
                                                {column.getValue(
                                                    serviceAccount,
                                                )}
                                            </TableCell>
                                        ),
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {serviceAccountList && serviceAccounts.length === 0 && (
                <Message variant="info" message={t('errors.noData')} />
            )}

            {serviceAccountListLoading && !serviceAccountList && (
                <>
                    <OffScreenAnnouncement
                        type="polite"
                        message={t(
                            'settings.userManagement.tableHeaders.loading',
                        )}
                        changeTrigger={1}
                    />
                    <SkeletonTable rowNum={6} />
                </>
            )}

            {serviceAccountListError && (
                <Message
                    variant="error"
                    message={t('reports.error.dataDidNotLoad')}
                />
            )}
        </>
    );
};
